/**
 * Profile Questionnaire — lifecycle authority helpers.
 *
 * Per ACT §7 ("the frontend does not decide canonical profile
 * activation"), the server is the canonical source of
 * `profile_status` / `missing_activation_fields`. This module is
 * NOT a second lifecycle engine — it is a strict extractor that
 * the reducer calls from `ExistingProfileLoaded` and
 * `SubmitSucceeded`.
 *
 * Behaviour:
 *   - PROFILE_LIFECYCLE_AUTHORITY: a sentinel constant that the
 *     reducer + tests assert on. The value is `'server'`; the FE
 *     never invents activation.
 *   - consumeLifecycleFromServer: takes the server's two lifecycle
 *     fields and returns the canonical pair. Unknown
 *     `profile_status` is collapsed to `'inactive'` and reported
 *     via `protocolViolations` so the reducer can surface it
 *     explicitly (not silently substitute it).
 */

export const PROFILE_LIFECYCLE_AUTHORITY = 'server' as const;

export type ProfileStatus = 'inactive' | 'active' | 'blocked' | 'deleted';

export const PROFILE_STATUSES: readonly ProfileStatus[] = Object.freeze([
  'inactive',
  'active',
  'blocked',
  'deleted',
]);

/**
 * Canonical lifecycle shape consumed by the reducer.
 *
 * `protocolViolations` is non-empty when the server returned a
 * value the FE cannot trust. The reducer MUST surface these
 * (typically via `model.serverError`) and MUST NOT pretend they
 * are valid lifecycle states.
 */
export interface ConsumedLifecycle {
  readonly profileStatus: ProfileStatus;
  readonly missingActivationFields: readonly string[];
  readonly protocolViolations: readonly string[];
}

const PROFILE_STATUS_SET: ReadonlySet<ProfileStatus> = new Set(PROFILE_STATUSES);

/**
 * The source the lifecycle payload came from. Controls whether a
 * missing `profile_status` is treated as a legitimate "no answer
 * yet" (CREATE-mode-before-first-save) or as a malformed server
 * response that the FE MUST surface.
 *
 * - `'initial'`: pre-save or local default; absent status is OK
 *   and silently collapses to 'inactive' (no violation raised).
 *   Use this when constructing an empty model from session or
 *   when no server payload has arrived yet.
 * - `'server-response'`: a real response came back from the
 *   server. The server MUST have provided a canonical status; if
 *   it didn't, that is a server bug and the FE MUST surface it
 *   via `protocolViolations` so the reducer can render it as a
 *   user-visible error. Used by `ExistingProfileLoaded` and
 *   `SubmitSucceeded`.
 */
export type LifecycleSource = 'initial' | 'server-response';

/**
 * Consume the server's lifecycle payload. Strict normalization:
 *
 *   - `profile_status` canonical (one of four) → value as-is.
 *   - `profile_status` unknown (e.g. a typo'd enum value, an
 *     object, a number) → collapsed to `'inactive'` AND surfaced
 *     via `protocolViolations`.
 *   - `profile_status` missing (`undefined`/`null`) and
 *     `mode === 'initial'` → collapsed to `'inactive'`, no
 *     violation (CREATE-mode-before-first-save).
 *   - `profile_status` missing (`undefined`/`null`) and
 *     `mode === 'server-response'` → collapsed to `'inactive'`
 *     AND surfaced via `protocolViolations`. A server response
 *     without `profile_status` is malformed; the FE MUST NOT
 *     silently treat it as "user is inactive".
 *   - `missing_activation_fields` not an array → empty array;
 *     if non-null/undefined, also surfaced as a violation.
 *
 * The reducer MUST NOT silently coerce unknown statuses. Either
 * the value is canonical (one of four) or the FE surfaces the
 * violation; there is no third option.
 */
export function consumeLifecycleFromServer(
  input: {
    readonly profile_status: unknown;
    readonly missing_activation_fields: unknown;
  },
  mode: LifecycleSource = 'server-response'
): ConsumedLifecycle {
  const violations: string[] = [];
  let profileStatus: ProfileStatus = 'inactive';

  if (
    typeof input.profile_status === 'string' &&
    PROFILE_STATUS_SET.has(input.profile_status as ProfileStatus)
  ) {
    profileStatus = input.profile_status as ProfileStatus;
  } else if (input.profile_status === undefined || input.profile_status === null) {
    if (mode === 'server-response') {
      // A real server response without a status is malformed. The
      // FE MUST NOT pretend the user is 'inactive'; that would
      // mask backend bugs and could mislead the user about their
      // own state. Surface it.
      violations.push(
        'server response is missing profile_status'
      );
    }
    // else: 'initial' mode — absent is OK, collapse silently.
    profileStatus = 'inactive';
  } else {
    violations.push(
      `unknown profile_status: ${JSON.stringify(input.profile_status)}`
    );
  }

  let missingActivationFields: readonly string[] = [];
  if (Array.isArray(input.missing_activation_fields)) {
    missingActivationFields = input.missing_activation_fields.filter(
      (v): v is string => typeof v === 'string'
    );
  } else if (
    input.missing_activation_fields !== undefined &&
    input.missing_activation_fields !== null
  ) {
    violations.push(
      `missing_activation_fields is not an array: ${JSON.stringify(input.missing_activation_fields)}`
    );
  }

  return {
    profileStatus,
    missingActivationFields,
    protocolViolations: violations,
  };
}

/**
 * Helper for tests + diagnostics: is this profile status the
 * canonical active one? Server-controlled; this helper exists so
 * the reducer never inlines `'active'` checks (DRY for the strict
 * invariant that FE cannot derive it).
 */
export function isActiveStatus(s: ProfileStatus): boolean {
  return s === 'active';
}

/**
 * Helper for tests + diagnostics: is this profile status blocked
 * or deleted (terminal)? The reducer uses this when surfacing
 * server-returned lifecycle in the view.
 */
export function isTerminalStatus(s: ProfileStatus): boolean {
  return s === 'blocked' || s === 'deleted';
}